import React, { createContext, useContext, useState, ReactNode } from 'react';

type ModalType = 'alert' | 'confirm';

interface ModalContextType {
    isOpen: boolean;
    type: ModalType;
    title: string;
    message: string;
    showAlert: (title: string, message: string) => void;
    showConfirm: (title: string, message: string, onConfirm: () => void) => void;
    closeModal: () => void;
    confirmAction: () => void;
}

const ModalContext = createContext<ModalContextType | undefined>(undefined);

export const ModalProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [type, setType] = useState<ModalType>('alert');
    const [title, setTitle] = useState('');
    const [message, setMessage] = useState('');
    const [onConfirm, setOnConfirm] = useState<(() => void) | null>(null);

    const showAlert = (title: string, message: string) => {
        setType('alert');
        setTitle(title);
        setMessage(message);
        setIsOpen(true);
        setOnConfirm(null);
    };

    const showConfirm = (title: string, message: string, onConfirmAction: () => void) => {
        setType('confirm');
        setTitle(title);
        setMessage(message);
        setIsOpen(true);
        setOnConfirm(() => onConfirmAction);
    };

    const closeModal = () => {
        setIsOpen(false);
        setTitle('');
        setMessage('');
        setOnConfirm(null);
    };

    const confirmAction = () => {
        if (onConfirm) {
            onConfirm();
        }
        closeModal();
    };

    return (
        <ModalContext.Provider value={{ isOpen, type, title, message, showAlert, showConfirm, closeModal, confirmAction }}>
            {children}
        </ModalContext.Provider>
    );
};

export const useModal = () => {
    const context = useContext(ModalContext);
    if (context === undefined) {
        throw new Error('useModal must be used within a ModalProvider');
    }
    return context;
};
